import React, { useState } from "react";
import { Paper, Typography, Divider, Button, IconButton, Box } from "@mui/material";
import { Add, Delete } from "@mui/icons-material";
import { GridComponent, ColumnsDirective, ColumnDirective, Inject, Sort } from '@syncfusion/ej2-react-grids';
import { AddClaimDialog } from '../dialogs/AddClaimDialog';

interface Claim {
    value: string;
    name: string;
    module?: string;
    description?: string;
}

interface ClaimsAccessGridProps {
    claimsAccess: Claim[];
    availableClaims: Claim[];
    onAdd?: (claim: Claim) => void;
    onDelete?: (claim: Claim) => void;
}

export const ClaimsAccessGrid: React.FC<ClaimsAccessGridProps> = ({ claimsAccess, availableClaims, onAdd, onDelete }) => {
    const [dialogOpen, setDialogOpen] = useState(false);
    const [selectedClaim, setSelectedClaim] = useState<Claim | null>(null);

    // Filter out claims already in claimsAccess
    const claimsToAdd = availableClaims.filter(
        (claim) => !claimsAccess.some((c) => c.value === claim.value)
    );

    const handleAddClick = () => setDialogOpen(true);
    const handleDialogClose = () => {
        setDialogOpen(false);
        setSelectedClaim(null);
    };
    const handleClaimSelect = (claim: Claim) => setSelectedClaim(claim);
    const handleAddConfirm = () => {
        if (selectedClaim && onAdd) {
            onAdd(selectedClaim);
            handleDialogClose();
        }
    };

    return (
        <Paper elevation={3} sx={{ p: 3, mt: 3, minWidth: 600 }}>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                <Typography variant="h6" gutterBottom>Claims</Typography>
                {onAdd && (
                    <Button variant="outlined" startIcon={<Add />} onClick={handleAddClick} size="small">
                        Add
                    </Button>
                )}
            </Box>
            <Divider sx={{ mb: 2 }} />
            <GridComponent
                dataSource={claimsAccess}
                allowPaging={false}
                allowSorting={true}
                rowHeight={32}
                allowSelection={false}
                height={360}
            >
                <ColumnsDirective>
                    <ColumnDirective field="name" headerText="Name" width={200} />
                    {onDelete && (
                        <ColumnDirective
                            headerText="Actions"
                            width={100}
                            template={(props: Claim) => (
                                <IconButton color="error" onClick={() => onDelete(props)}>
                                    <Delete />
                                </IconButton>
                            )}
                            textAlign="Center"
                        />
                    )}
                </ColumnsDirective>
                <Inject services={[Sort]} />
            </GridComponent>
            <AddClaimDialog
                open={dialogOpen}
                claimsToAdd={claimsToAdd}
                selectedClaim={selectedClaim}
                onSelect={handleClaimSelect}
                onClose={handleDialogClose}
                onAdd={handleAddConfirm}
            />
        </Paper>
    );
};